import type { ComponentChildren } from 'preact'
import { useCallback, useState } from 'preact/hooks'
import { useLocation } from 'wouter-preact'
import { calculateWeightTrend } from '~/calculations/weightTrends'
import { DecisionPanel } from '~/components/feedback/DecisionPanel'
import { ExportControls } from '~/components/feedback/ExportControls'
import { SecondOpinion } from '~/components/feedback/SecondOpinion'
import { Screen } from '~/components/layout/Screen'
import { Button } from '~/components/primitives/Button'
import { useAsyncData } from '~/hooks/useAsyncData'
import type { RulesOutcome } from '~/models/rulesOpinion'
import { nutritionSummaryRequired, reviewStepsFor } from '~/models/weeklyReview'
import { NEXT_PHASE_ROUTE } from '~/routes'
import { getActiveNutritionPlan } from '~/services/dailyCheckService'
import { getActiveDomains } from '~/services/domainOpinionService'
import {
  buildWeeklyCsvZip,
  buildWeeklyMediaZip,
  buildWeeklyReportModel,
  deliverCsvZip,
  deliverMediaZip,
  deliverWeeklyPdf,
  deliverWeeklyReport,
  renderWeeklyPdfReport,
  renderWeeklyReport,
} from '~/services/exportService'
import { getNextPhaseState } from '~/services/nextPhaseService'
import { getOnboardingState } from '~/services/onboardingService'
import { startOrResumeWeeklyReview } from '~/services/reviewService'
import { getWeeklyOutcome } from '~/services/rulesService'
import { getLiftSummaries } from '~/services/strengthService'
import { getTrainingCountsForWeek } from '~/services/trainingWeekService'
import { getTrendObservations, todayFor } from '~/services/weightService'
import { useDatabase } from '~/storage/DatabaseContext'
import {
  getLatestMeasurementSession,
  listMeasurementSessions,
} from '~/storage/repositories/measurementRepository'
import { getSettings } from '~/storage/repositories/settingsRepository'
import { STORE } from '~/storage/schema'
import { formatCalendarDate } from '~/utils/format'
import { MeasurementCapture } from './MeasurementCapture'
import styles from './ReviewPage.module.css'
import { WeeklyReviewWizard } from './wizard/WeeklyReviewWizard'

/**
 * The weekly review.
 *
 * From Checkpoint 2.1 this screen hosts the guided wizard rather than being a
 * measurement form. It loads what the wizard reads and gets out of the way; the
 * wizard owns the draft, the resume position, the update deferral and the draft
 * saver, and `MeasurementCapture` owns the entry form it renders as one step.
 *
 * Photo capture (Checkpoint 3) is not a step. A step that named an unbuilt
 * feature would be a mock; the final step says where it lands instead.
 */
export function ReviewPage() {
  const db = useDatabase()
  const [, navigate] = useLocation()
  const [measurementsPending, setMeasurementsPending] = useState(false)

  const { state, reload } = useAsyncData(async () => {
    const now = new Date()
    const reference = todayFor(now)

    /* Read FIRST, and on its own, because `startOrResumeWeeklyReview` below
       WRITES: it creates a draft when none exists. A review that is paused must
       not leave a draft behind that can never be finalized — the user did not
       start a week, and a stored record saying they did is the "name unbuilt
       capability rather than mocking it" rule broken with a side effect
       (D-103 §5). */
    const nextPhase = await getNextPhaseState(db)
    const paused = nextPhase.ok && nextPhase.value.available

    const [
      onboarding,
      settings,
      latest,
      sessions,
      outcome,
      observations,
      session,
      lifts,
      domains,
      nutritionPlan,
    ] = await Promise.all([
      getOnboardingState(db),
      getSettings(db, now),
      getLatestMeasurementSession(db),
      listMeasurementSessions(db),
      getWeeklyOutcome(db, now),
      getTrendObservations(db, reference),
      paused ? undefined : startOrResumeWeeklyReview(db, now),
      getLiftSummaries(db, now),
      getActiveDomains(db),
      getActiveNutritionPlan(db),
    ])
    const trend = observations.ok ? calculateWeightTrend(observations.value, reference) : undefined

    /* Read after the review, because it needs that review's own week start — and
       a review can cover a week that has already ended. `getTrainingCountsForWeek`
       never materialises for exactly that reason (D-105 §6). */
    const training =
      session === undefined || !session.ok || session.value.kind === 'projectNotStarted'
        ? undefined
        : await getTrainingCountsForWeek(db, session.value.review.weekStartDate)

    return {
      training,
      onboarding,
      settings,
      latest,
      sessions,
      outcome,
      trend,
      session,
      lifts,
      domains,
      nutritionPlan,
    }
    /* `weeklyReviews` is deliberately NOT an invalidation key.

       The wizard writes the draft on every step transition, and re-reading this
       page on each of those writes loses the wizard's position mid-step. The
       wizard owns the draft; this page re-reads only when something else changes
       or when `reload` is called explicitly. Verified: re-adding the key fails
       three browser tests. */
  }, [STORE.profile, STORE.measurementSessions, STORE.settings, STORE.weightEntries, STORE.phases])

  // Stable identity: `MeasurementCapture` reports through this from an effect.
  const onPendingChange = useCallback((pending: boolean) => {
    setMeasurementsPending(pending)
  }, [])

  if (state.status === 'loading') {
    return (
      <Screen title="Weekly Review">
        <p>Loading…</p>
      </Screen>
    )
  }

  if (state.status === 'error') {
    return (
      <Screen title="Weekly Review">
        <p role="alert">Project Sola could not read your data. {state.message}</p>
      </Screen>
    )
  }

  const {
    onboarding,
    settings,
    latest,
    sessions,
    outcome,
    trend,
    session,
    lifts,
    domains,
    nutritionPlan,
    training,
  } = state.value

  /* First run only (D-103 §2). A review with no active phase still runs: the
     step list is computed from the user's own active domains, and the decision
     step's `contextUnavailable` state names the gap rather than inventing an
     opinion about it. */
  if (!onboarding.ok || !settings.ok || onboarding.value.status === 'firstRun') {
    return (
      <Screen title="Weekly Review">
        <p class={styles.lede}>
          Measurements need your height first. Set up Project Sola on the Today screen.
        </p>
      </Screen>
    )
  }

  /**
   * Between phases (D-103 §5).
   *
   * **The review pauses and states the reason.** Project Sola has no phase to
   * judge a week against, so there is no opinion to form — and running the
   * wizard to a `contextUnavailable` panel would ask for a week of answers and
   * then say it could not read them, which is worse than not asking.
   *
   * **A phase-free ruleset was rejected** (§5): there is none in
   * `rules/registry.ts`, and writing one means inventing coaching advice
   * belonging to no phase, which D-043's selectability gate exists to prevent.
   *
   * **Recording is unaffected.** Weigh-ins, measurements and photos all continue
   * — none of them depends on a phase, and losing them would charge the user for
   * a decision they have not made yet. Only the *judging* stops.
   *
   * This is not the cardio-only case. `available` requires a **finished phase**
   * in the history, so a user who never activated body composition has their
   * review run normally, judged by their own domains.
   *
   * **Gated on the absent session rather than on a second flag.** The loader
   * skips `startOrResumeWeeklyReview` when paused, so `session === undefined`
   * IS the paused state — carrying a separate boolean beside it would be two
   * sources of truth that can disagree, and this way the type system stops
   * everything below from reading a review that was never started.
   */
  if (session === undefined) {
    return (
      <Screen title="Weekly Review">
        <section aria-labelledby="paused-heading" class={styles.paused}>
          <h2 id="paused-heading" class={styles.pausedHeading}>
            Paused until you choose your next phase
          </h2>
          <p>
            Your last phase has ended, so Project Sola has nothing to judge this week against. It is
            not forming an opinion rather than guessing at one.
          </p>
          <p>
            <strong>Keep recording.</strong> Your weigh-ins, measurements and photos are all still
            being kept, and the weeks you have already reviewed are unchanged.
          </p>
          <Button onClick={() => navigate(NEXT_PHASE_ROUTE)}>Choose your next phase</Button>
        </section>
      </Screen>
    )
  }

  const measurementStep = (
    <MeasurementCapture
      settings={settings.value}
      latest={latest.ok ? latest.value : undefined}
      sessions={sessions.ok ? sessions.value : []}
      onSaved={reload}
      onPendingChange={onPendingChange}
    />
  )

  const exportControls = (
    <ExportControls
      onExport={async (selection, format) => {
        const model = await buildWeeklyReportModel(db, new Date(), selection)
        if (!model.ok) {
          throw new Error(
            'message' in model.error
              ? model.error.message
              : 'Project Sola could not read your data.',
          )
        }

        if (format === 'json') {
          await deliverWeeklyReport(renderWeeklyReport(model.value))
          return
        }

        if (format === 'csv') {
          const csv = await buildWeeklyCsvZip(model.value)
          if (!csv.ok) {
            throw new Error(
              'message' in csv.error
                ? csv.error.message
                : 'Project Sola could not create the spreadsheet.',
            )
          }
          await deliverCsvZip(csv.value)
          return
        }

        if (format === 'media') {
          const zip = await buildWeeklyMediaZip(db, model.value)
          if (!zip.ok) {
            throw new Error(
              'message' in zip.error
                ? zip.error.message
                : 'Project Sola could not create the photo file.',
            )
          }
          await deliverMediaZip(zip.value)
          return
        }

        /* Drawing can fail for a reason the user can act on — a PDF too large
           to open — so it returns a Result rather than throwing (D-082). */
        const pdf = await renderWeeklyPdfReport(db, model.value)
        if (!pdf.ok) {
          throw new Error(
            'message' in pdf.error ? pdf.error.message : 'Project Sola could not draw the PDF.',
          )
        }
        await deliverWeeklyPdf(pdf.value)
      }}
    />
  )

  /* Ordered so each state narrows the next, and the trend check stays last.
     A finished week does not need a trend — it renders `FinishedWeek`, which
     never reads one — so the trend requirement must not move above these. */
  if (!session.ok) {
    return (
      <Screen title="Weekly Review">
        <p role="alert">Project Sola could not open this week's review.</p>
        {measurementStep}
      </Screen>
    )
  }

  /* The project has not begun (D-107 §4).
     A named state, not the error below: nothing failed, and the screen says when
     the first review will be available rather than reporting a problem. */
  if (session.value.kind === 'projectNotStarted') {
    const startsOn = formatCalendarDate(session.value.startsOn)
    return (
      <Screen title="Weekly Review">
        <p>Your project starts on {startsOn}, so there is no week to review yet.</p>
        <p>
          Your first weekly review will be available at the end of that first week. Anything you
          record before then is kept.
        </p>
      </Screen>
    )
  }

  if (session.value.kind === 'alreadyComplete') {
    return (
      <FinishedWeek
        outcome={outcome.ok ? outcome.value : undefined}
        exportControls={exportControls}
      />
    )
  }

  if (trend === undefined) {
    return (
      <Screen title="Weekly Review">
        <p role="alert">Project Sola could not open this week's review.</p>
        {measurementStep}
      </Screen>
    )
  }

  /* A read that failed leaves the review as it was before domains existed, rather
     than as an empty wizard. Losing a screen is worse than showing one step too
     many, and every project that predates schema v3 is a body-composition one. */
  const active =
    domains.ok && domains.value.length > 0 ? domains.value : (['bodyComposition'] as const)

  const plan = nutritionPlan.ok ? nutritionPlan.value : undefined

  /*
   * No export control renders while the review is unfinished (D-104 §5).
   *
   * **Absent, not disabled.** A partially completed review was exportable until
   * C7.4 phase B, which meant a file could carry a week whose evidence the user
   * was still in the middle of entering — and Project Sola's own opinion is the
   * first block in that file. Disabling the control would leave the offer on
   * screen and make the user work out why it does not respond; removing it says
   * the same thing by being true.
   */
  return (
    <Screen title="Weekly Review">
      <WeeklyReviewWizard
        review={session.value.review}
        trend={trend}
        unit={settings.value.weightDisplayUnit}
        outcome={outcome.ok ? outcome.value : undefined}
        measurementStep={measurementStep}
        measurementsPending={measurementsPending}
        lifts={lifts.ok ? lifts.value : []}
        /* Computed here rather than inside the wizard, which may not read
           storage. A user with no domain goals at all — every project created
           before schema v3 has one, so this is a failed read rather than an
           ordinary state — falls back to the body-composition review, which is
           what every such project was. */
        steps={reviewStepsFor({
          bodyCompositionActive: active.includes('bodyComposition'),
          strengthActive: active.includes('strength'),
          /* Only an external app whose daily questions are off needs asking here;
             every other source has already answered the week day by day. */
          nutritionSummaryRequired: nutritionSummaryRequired(plan),
        })}
        {...(plan?.providerLabel === undefined
          ? {}
          : { nutritionProviderLabel: plan.providerLabel })}
        {...(training !== undefined && training.ok && training.value !== undefined
          ? { derivedTraining: training.value }
          : {})}
        onFinalized={reload}
        onReload={reload}
      />
    </Screen>
  )
}

/**
 * A week already finished is not reopened (D-046).
 *
 * The opinion it was given stays readable and the export it feeds stays
 * available. What is gone is the ability to change the evidence underneath a
 * conclusion the app has already given.
 *
 * **The order is fixed and is the mechanism** (D-104 §5, D-008, D-093):
 * Project Sola's recommendation, then the second opinion, then one export
 * button. Two browser tests assert it by document order rather than by pixels.
 * **This is also the only screen an export is reachable from** — the review has
 * to be finished before a file can be made of it.
 */
function FinishedWeek({
  outcome,
  exportControls,
}: {
  outcome: RulesOutcome | undefined
  exportControls: ComponentChildren
}) {
  return (
    <Screen title="Weekly Review">
      <p class={styles.lede}>This week's review is finished. Project Sola's read of it is below.</p>
      {outcome === undefined ? null : <DecisionPanel outcome={outcome} />}
      {/* Below the decision panel, and that order is the mechanism rather than a
          layout choice: the app's own answer is on screen before the offer to
          ask anyone else, so a second opinion is always second (D-008, D-093). */}
      <SecondOpinion />
      {exportControls}
      <p class={styles.pending}>
        Corrections to a finished week are not available yet. The next review opens at the start of
        your next week.
      </p>
    </Screen>
  )
}
